import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { formatDate } from "@/lib/prefs";
import { Plus, Pencil, Trash2, Loader2, PenLine, Save, ArrowLeft, ExternalLink, Eye, Users } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ImageUpload from "@/components/ImageUpload";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

function slugify(t) { return (t || "").toLowerCase().replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-"); }

export function BlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/blog"); setPosts(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/blog/${id}`);
    toast.success("Deleted");
    load();
  };
  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-6xl mx-auto space-y-6" data-testid="blog-list-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><PenLine className="w-3 h-3" /> Journal</div>
          <h1 className="font-display text-3xl md:text-4xl leading-none mt-2">Blog</h1>
          <p className="text-muted-foreground mt-2 text-sm">High-end culinary technique articles. Published posts appear on your public <Link to="/blog" className="gold underline">/blog</Link>.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild data-testid="blog-view-public"><Link to="/blog" target="_blank" className="gap-2"><ExternalLink className="w-3.5 h-3.5" /> View public</Link></Button>
          <Button asChild className="gap-2" data-testid="blog-new-btn"><Link to="/admin/blog/new"><Plus className="w-4 h-4" /> New post</Link></Button>
        </div>
      </header>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : (
        <div className="rounded-2xl glass-card overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left border-b border-border">
                <th className="px-4 py-3 label-eyebrow">Title</th>
                <th className="px-4 py-3 label-eyebrow">Slug</th>
                <th className="px-4 py-3 label-eyebrow">Status</th>
                <th className="px-4 py-3 label-eyebrow">Updated</th>
                <th className="px-4 py-3 label-eyebrow text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => (
                <tr key={p.id} className="border-b border-border/60 hover:bg-secondary/40" data-testid={`blog-row-${p.id}`}>
                  <td className="px-4 py-3 font-medium">{p.title}</td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">/{p.slug}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${p.published ? "bg-primary/15 gold" : "bg-secondary text-muted-foreground"}`}>
                      {p.published ? "Published" : "Draft"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{formatDate(p.updated_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button variant="ghost" size="sm" asChild data-testid={`blog-edit-${p.id}`}><Link to={`/admin/blog/${p.id}`}><Pencil className="w-4 h-4" /></Link></Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(p.id)} data-testid={`blog-delete-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
              {!posts.length && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">No posts yet. Write your first article.</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}

const empty = { title: "", slug: "", excerpt: "", body: "", hero_image: "", tags: [], published: false, contributor_id: "" };

export function BlogEditor() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [contributors, setContributors] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/contributors");
        setContributors(data);
      } catch { /* non-fatal */ }
    })();
  }, []);

  useEffect(() => {
    if (!isNew) {
      (async () => {
        try {
          const { data } = await api.get(`/blog/${id}`);
          setForm({ ...empty, ...data });
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
          navigate("/admin/blog");
        } finally { setLoading(false); }
      })();
    }
  // eslint-disable-next-line
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setTitle = (v) => {
    setForm((f) => ({ ...f, title: v, slug: (isNew || !f.slug || f.slug === slugify(f.title)) ? slugify(v) : f.slug }));
  };

  const save = async (publish) => {
    setSaving(true);
    try {
      const payload = { ...form, tags: Array.isArray(form.tags) ? form.tags : String(form.tags).split(",").map((t) => t.trim()).filter(Boolean), published: publish ?? form.published };
      if (isNew) {
        const { data } = await api.post("/blog", payload);
        toast.success(payload.published ? "Post published" : "Draft saved");
        navigate(`/admin/blog/${data.id}`);
      } else {
        const { data } = await api.patch(`/blog/${id}`, payload);
        setForm({ ...empty, ...data });
        toast.success(payload.published ? "Published" : "Saved");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-4xl mx-auto space-y-6" data-testid="blog-editor-page">
      <div className="flex items-center justify-between gap-3">
        <Link to="/admin/blog" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="blog-back">
          <ArrowLeft className="w-4 h-4" /> All posts
        </Link>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setPreview((v) => !v)} className="gap-2" data-testid="blog-preview-toggle"><Eye className="w-4 h-4" /> {preview ? "Edit" : "Preview"}</Button>
          <Button variant="outline" onClick={() => save(false)} disabled={saving} className="gap-2" data-testid="blog-save-draft"><Save className="w-4 h-4" /> Save draft</Button>
          <Button onClick={() => save(true)} disabled={saving} className="gap-2" data-testid="blog-publish">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Publish"}</Button>
        </div>
      </div>

      {preview ? (
        <article className="prose prose-invert max-w-none">
          <div className="label-eyebrow">{form.tags?.join(" • ") || "draft"}</div>
          <h1 className="font-display text-4xl mt-2 mb-4">{form.title || "Untitled"}</h1>
          {form.hero_image && <img src={form.hero_image.startsWith("http") ? form.hero_image : `${process.env.REACT_APP_BACKEND_URL}${form.hero_image}`} alt="" className="rounded-lg w-full max-h-96 object-cover mb-6" />}
          {form.excerpt && <p className="text-muted-foreground italic">{form.excerpt}</p>}
          <div className="prose prose-invert prose-headings:font-display max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.body || "*(nothing yet)*"}</ReactMarkdown>
          </div>
        </article>
      ) : (
        <div className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input value={form.title} onChange={(e) => setTitle(e.target.value)} data-testid="blog-title" />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Slug</Label>
              <Input value={form.slug} onChange={(e) => set("slug", e.target.value)} data-testid="blog-slug" />
            </div>
            <div>
              <Label>Tags (comma-separated)</Label>
              <Input value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={(e) => set("tags", e.target.value)} data-testid="blog-tags" placeholder="sous-vide, plating, brigade" />
            </div>
          </div>
          <ImageUpload value={form.hero_image} onChange={(v) => set("hero_image", v)} label="Hero image" testId="blog-hero" />
          <div>
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-1"><Users className="w-3 h-3" /> Guest chef byline</Label>
              <Link to="/admin/contributors" className="text-xs gold hover:brightness-110" data-testid="blog-manage-contributors">Manage contributors →</Link>
            </div>
            <Select value={form.contributor_id || "__none__"} onValueChange={(v) => set("contributor_id", v === "__none__" ? "" : v)}>
              <SelectTrigger data-testid="blog-contributor"><SelectValue placeholder="No guest: house byline" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No guest: house byline</SelectItem>
                {contributors.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name} • {c.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Excerpt</Label>
            <Textarea rows={2} value={form.excerpt || ""} onChange={(e) => set("excerpt", e.target.value)} data-testid="blog-excerpt" placeholder="One or two sentences to preview the article." />
          </div>
          <div>
            <Label>Body (Markdown)</Label>
            <Textarea rows={16} value={form.body} onChange={(e) => set("body", e.target.value)} className="font-mono text-sm" data-testid="blog-body" placeholder="# Heading\n\nParagraph…" />
          </div>
          <div className="flex items-center justify-between border border-border rounded p-3">
            <div><div className="text-sm font-medium">Published</div><div className="text-xs text-muted-foreground">Visible on public /blog</div></div>
            <Switch checked={!!form.published} onCheckedChange={(v) => set("published", v)} data-testid="blog-published" />
          </div>
        </div>
      )}
    </div>
  );
}
